import React, { useState, useEffect, Suspense } from 'react';
import { Switch, Route, Redirect, Link, useLocation } from 'react-router-dom';
import { Notification } from '@eds/vanilla';
import {
  SystemBar,
  SystemBarComponent,
  AppContainer,
  AppBar,
  AppBody,
  Navigation,
  NavItem,
  NavGroup,
  NotificationTrigger,
  NotificationLogPanel,
  NotificationLogItem,
  useNotification,
  useMultiPageLayout,
} from '@dsi/react-eds';
// import { version } from '../package.json';
import * as localStorage from './lib/localStorage';
import SysPanel from './components/SysPanel';
//import Loader from './components/Loader';
import TilesPage from './pages/Tiles';

import './App.css';

const AccordionPage = React.lazy(() => import('./pages/Accordion'));
const ButtonsPage = React.lazy(() => import('./pages/Buttons'));
const CardsPage = React.lazy(() => import('./pages/Cards'));
const CheckboxesPage = React.lazy(() => import('./pages/Checkboxes'));
const DropdownsPage = React.lazy(() => import('./pages/Dropdowns'));
const RadioButtonsPage = React.lazy(() => import('./pages/RadioButtons'));
const SwitchesPage = React.lazy(() => import('./pages/Switches'));
const RangeSlidersPage = React.lazy(() => import('./pages/RangeSliders'));
const TablesPage = React.lazy(() => import('./pages/Tables'));
const DialogsPage = React.lazy(() => import('./pages/Dialogs'));
const TreesPage = React.lazy(() => import('./pages/Trees'));
const SelectsPage = React.lazy(() => import('./pages/Selects'));
const TextFieldsPage = React.lazy(() => import('./pages/TextFields'));
const NumberFieldsPage = React.lazy(() => import('./pages/NumberFields'));
const DateFieldsPage = React.lazy(() => import('./pages/Datefields'));
const LineSingleSeriesPage = React.lazy(() => import('./pages/LineSingleSeries'));
const LineMultiSeriesPage = React.lazy(() => import('./pages/LineMultiSeries'));
const PaginationPage = React.lazy(() => import('./pages/Pagination'));
const PillsPage = React.lazy(() => import('./pages/Pills'));
const TabsPage = React.lazy(() => import('./pages/Tabs'));
const TooltipsPage = React.lazy(() => import('./pages/Tooltips'));
// Maximize tile does not work if Tile is lazy loaded.
// Potentially because of this https://github.com/facebook/react/issues/17470
// const TilesPage = React.lazy(() => import('./pages/Tiles'));
const MultiPanelTilePage = React.lazy(() => import('./pages/MultiPanelTile'));
const ProgressBarsPage = React.lazy(() => import('./pages/ProgressBars'));
const VerticalBarSingleSeriesPage = React.lazy(() => import('./pages/VerticalBarSingleSeries'));
const VerticalBarGroupedSeriesPage = React.lazy(() => import('./pages/VerticalBarGroupedSeries'));
const VerticalBarStackedSeriesPage = React.lazy(() => import('./pages/VerticalBarStackedSeries'));
const HorizontalBarSingleSeriesPage = React.lazy(() => import('./pages/HorizontalBarSingleSeries'));
const HorizontalBarGroupedSeriesPage = React.lazy(() => import('./pages/HorizontalBarGroupedSeries'));
const HorizontalBarStackedSeriesPage = React.lazy(() => import('./pages/HorizontalBarStackedSeries'));
const HistogramSingleSeriesPage = React.lazy(() => import('./pages/HistogramSingleSeries'));
const HistogramMultiSeriesPage = React.lazy(() => import('./pages/HistogramMultiSeries'));
const GaugePage = React.lazy(() => import('./pages/Gauge'));
const DonutPage = React.lazy(() => import('./pages/Donut'));
const ExampleSimplePage = React.lazy(() => import('./pages/ExampleSimple'));
const ExampleMultiPage = React.lazy(() => import('./pages/ExampleMulti'));
const AboutPage = React.lazy(() => import('./pages/About'));
const LoadingPage = React.lazy(() => import('./pages/Loading'));
const NotificationLogPage = React.lazy(() => import('./pages/NotificationLog'));
const WizardPage = React.lazy(() => import('./pages/Wizard'));
const FlyoutPage = React.lazy(() => import('./pages/Flyout'));
const EmptyStatePage = React.lazy(() => import('./pages/EmptyState'));
const NavigationPage = React.lazy(() => import('./pages/Navigation'));
const AppBodyPage = React.lazy(() => import('./pages/AppBody'));
const AppBarPage = React.lazy(() => import('./pages/AppBar'));
const SystemBarPage = React.lazy(() => import('./pages/SystemBar'));
const HooksPage = React.lazy(() => import('./pages/Hooks'));
const TimePickerPage = React.lazy(() => import('./pages/TimePicker'));
const SearchFieldsPage = React.lazy(() => import('./pages/SearchFields'));
const BreadcrumbsPage = React.lazy(() => import('./pages/Breadcrumbs'));
const BubbleSingleSeriesPage = React.lazy(() => import('./pages/BubbleSingleSeries'));
const BubbleMultiSeriesPage = React.lazy(() => import('./pages/BubbleMultiSeries'));
const RadarSingleSeriesPage = React.lazy(() => import('./pages/RadarSingleSeries'));
const RadarMultiSeriesPage = React.lazy(() => import('./pages/RadarMultiSeries'));
const ScatterPlotSingleSeriesPage = React.lazy(() => import('./pages/ScatterPlotSingleSeries'));
const ScatterPlotMultiSeriesPage = React.lazy(() => import('./pages/ScatterPlotMultiSeries'));
const ReleaseNotesPage = React.lazy(() => import('./pages/ReleaseNotes'));
const AppContainerPage = React.lazy(() => import('./pages/AppContainer'));
const TimelinePage = React.lazy(() => import('./pages/Timeline'));
const ProvidersPage = React.lazy(() => import('./pages/Providers'));
const HeatmapPage = React.lazy(() => import('./pages/Heatmap'));
const HierarchyChartPage = React.lazy(() => import('./pages/HierarchyChart'));
const StackedAreaChartPage = React.lazy(() => import('./pages/StackedAreaCharts'));
const LogicalViewChartPage = React.lazy(() => import('./pages/LogicalViewChart'));
const BoxPlotPage = React.lazy(() => import('./pages/BoxPlot'));
const ListBuilderPage = React.lazy(() => import('./pages/ListBuilder'));
const DataMarkersPage = React.lazy(() => import('./pages/DataMarkers'));
const TagsPage = React.lazy(() => import('./pages/Tags'));
const CarouselPage = React.lazy(() => import('./pages/Carousel'));

function randomKey() {
  return Math.random().toString(36).substr(2, 5);
}

function App() {
  const location = useLocation();
  const [currentPath, setCurrentPath] = useMultiPageLayout();
  const [sysPanel, setSysPanel] = useState({
    notifications: false,
    userSettings: false,
  });
  const showSysPanel = sysPanel.notifications || sysPanel.userSettings;
  const notificationTimestamps = localStorage.getObject('notifications', {
    new: Date.now(),
    viewed: Date.now(),
  });
  const {
    getNotifications,
    getNewNotificationCount,
    addNotifications,
    acknowledgeNotification,
    readNotification,
  } = useNotification([
    {
      title: 'React EDS v2.3.0 release',
      description: 'Compatible with EDS v3.11.0',
      timestamp: new Date('2021-10-14 16:00:00'),
    },
    {
      title: 'React EDS v2.2.0 release',
      description: 'Pass remaining props as attributes to Dialog div',
      timestamp: new Date('2021-08-10 17:00:00'),
    },
    {
      title: 'React EDS v2.1.0 release',
      description: 'Compatible with EDS v3.10.0.',
      timestamp: new Date('2021-07-25 23:00:00'),
    },
    {
      title: 'React EDS v2.0.0 release',
      description: 'Removal of draggable cards. New useTheme hook.',
      timestamp: new Date('2021-05-20 11:00:00'),
    },
    {
      title: 'React EDS v1.1.0 release',
      description: 'Compatible with EDS v3.9.0',
      timestamp: new Date('2021-04-03 16:00:00'),
    },
    {
      title: 'React EDS v1.0.0 release',
      description: 'Compatible with EDS v3.8.0',
      timestamp: new Date('2020-12-28 10:00:00'),
    },
    {
      title: 'React EDS v0.20.0 release',
      description: 'Align input-type components.',
      timestamp: new Date('2020-12-11 14:00:00'),
    },
    {
      title: 'React EDS v0.19.0 release',
      description: 'Updated Timeline component to EDS v3.7.0 specification.',
      timestamp: new Date('2020-11-21 11:00:00'),
    },
    {
      title: 'React EDS v0.18.0 release',
      description: 'Initial support for EDS v3.7.0',
      timestamp: new Date('2020-11-14 18:00:00'),
    },
    {
      title: 'React EDS v0.17.0 release',
      description: 'New date prop for Datepicker',
      timestamp: new Date('2020-10-23 13:00:00'),
    },
    {
      title: 'React EDS v0.16.0 release',
      description: 'FileUpload component',
      timestamp: new Date('2020-09-18 13:30:00'),
    },
    {
      title: 'React EDS v0.15.0 release',
      description: 'Compatible with EDS v3.6.1. Table header and cell alignment',
      timestamp: new Date('2020-09-07 15:00:00'),
    },
    {
      title: 'React EDS v0.14.0 release',
      description: 'ThemeProvider and Timeline components. No header table option.',
      timestamp: new Date('2020-08-11 20:00:00'),
    },
    {
      title: 'React EDS v0.13.0 release',
      description: 'AppContainer layout component.',
      timestamp: new Date('2020-07-28 16:30:00'),
    },
    {
      title: 'React EDS v0.12.0 release',
      description: 'Option to default hide navigation panel.',
      timestamp: new Date('2020-07-06 13:00:00'),
    },
    {
      title: 'React EDS v0.11.0 release',
      description: 'Compatible with EDS v3.6.0. New Breadcrumb, Bubble chart, Radar chart and Scatter plot components',
      timestamp: new Date('2020-06-26 12:30:00'),
    },
    {
      title: 'React EDS v0.10.0 release',
      description: 'Compatible with EDS v3.5.0.',
      timestamp: new Date('2020-04-21 11:00:00'),
    },
    {
      title: 'React EDS v0.9.0 release',
      description: 'No longer possible to hide menu by default.',
      timestamp: new Date('2020-04-16 10:45:00'),
    },
    {
      title: 'React EDS v0.8.0 release',
      description: 'Expandable table rows. Project is now written in Typescript.',
      timestamp: new Date('2020-04-11 16:00:00'),
    },
    {
      title: 'React EDS v0.7.0 release',
      description: 'Fixed table header. Updated documentation.',
      timestamp: new Date('2020-03-06 12:00:00'),
    },
    {
      title: 'React EDS v0.6.0 release',
      description: 'Updated pagination behavior',
      timestamp: new Date('2020-02-25 10:00:00'),
    },
    {
      title: 'React EDS v0.5.0 release',
      description: 'Table settings. Configurable MultiPanelTile panel widths.',
      timestamp: new Date('2019-12-30 12:00:00'),
    },
    {
      title: 'React EDS v0.4.0 release',
      description: 'Compatible with EDS v3.4.0. New flyout panel component',
      timestamp: new Date('2019-12-19 12:00:00'),
    },
    {
      title: 'React EDS v0.3.0 release',
      description: 'Drag and drop support for cards',
      timestamp: new Date('2019-12-17 12:00:00'),
    },
    {
      title: 'React EDS v0.2.0 release',
      description: 'Compatible with EDS v3.3.0',
      timestamp: new Date('2019-11-20 12:00:00'),
    },
    {
      title: 'React EDS v0.1.0 release',
      description: 'Initital release.',
      timestamp: new Date('2019-11-15 12:00:00'),
    },
  ], notificationTimestamps, showSysPanel);

  const notifications = getNotifications();
  const newNotifications = getNewNotificationCount();
  const toggleSysPanel = (type) => {
    if (type === 'notifications') {
      if (sysPanel.notifications) {
        readNotification();
        localStorage.saveObject('notifications', {
          new: Date.now(),
          viewed: Date.now(),
        });
      }
      setSysPanel({
        notifications: !sysPanel.notifications,
        userSettings: false,
      });
    } else {
      setSysPanel({
        notifications: false,
        userSettings: !sysPanel.userSettings,
      });
    }
  };
  const closeSysPanel = (type) => {
    setSysPanel({
      notifications: false,
      userSettings: false,
    });
    if (type === 'notifications') {
      readNotification();
      localStorage.saveObject('notifications', {
        new: Date.now(),
        viewed: Date.now(),
      });
    }
  };

  const pathToTitleMap = {
    '/about': { title: 'About' },
    '/example/simple-app': { title: 'Simple application' },
    '/example/multi-page-app': { title: 'Multi page application' },
    '/components/accordion': { title: 'Accordion' },
    '/components/breadcrumbs': { title: 'Breadcrumb' },
    '/components/trees': { title: 'Trees' },
    '/components/buttons': { title: 'Buttons' },
    '/components/cards': { title: 'Cards' },
    '/components/carousel': { title: 'Carousel' },
    '/components/checkboxes': { title: 'Checkboxes' },
    '/components/radiobuttons': { title: 'Radio buttons' },
    '/components/switch': { title: 'Switch' },
    '/components/dropdowns': { title: 'Dropdowns' },
    '/components/loading': { title: 'Loading' },
    '/components/rangesliders': { title: 'Range sliders' },
    '/components/selects': { title: 'Selects' },
    '/components/textfields': { title: 'Text fields' },
    '/components/time-picker': { title: 'Time picker' },
    '/components/numberfields': { title: 'Number fields' },
    '/components/datefields': { title: 'Date fields' },
    '/components/tables': { title: 'Tables' },
    '/components/pagination': { title: 'Pagination' },
    '/components/search': { title: 'Search fields' },
    '/components/pills': { title: 'Pills' },
    '/components/tabs': { title: 'Tabs' },
    '/components/tooltips': { title: 'Tooltips' },
    '/components/progress-bar': { title: 'Progress bar' },
    '/components/timeline': { title: 'Timeline' },
    '/components/tags': { title: 'Tags' },

    '/layouts/systembar': { title: 'System bar' },
    '/layouts/appcontainer': { title: 'App container' },
    '/layouts/appbar': { title: 'App bar' },
    '/layouts/appbody': { title: 'App body' },
    '/layouts/dialogs': { title: 'Dialogs' },
    '/layouts/empty-state': { title: 'Empty state' },
    '/layouts/list-builder': { title: 'List builder' },
    '/layouts/tiles': { title: 'Tiles' },
    '/layouts/multi-panel-tile': { title: 'Multi panel tile' },
    '/layouts/notification-log': { title: 'Notification log' },
    '/layouts/wizard': { title: 'Wizard' },
    '/layouts/flyout': { title: 'Flyout panel' },
    '/layouts/navigation': { title: 'Navigation' },

    '/data-visualization/gauge': { title: 'Gauge' },
    '/data-visualization/donut': { title: 'Donut' },
    '/data-visualization/box-plot': { title: 'Box plot' },
    '/data-visualization/bubble-chart/single-bubble-chart': { title: 'Bubble chart', subtitle: 'Single series' },
    '/data-visualization/bubble-chart/multi-bubble-chart': { title: 'Bubble chart', subtitle: 'Multi series' },
    '/data-visualization/line-chart/single-line-chart': { title: 'Line chart', subtitle: 'Single series' },
    '/data-visualization/line-chart/multi-line-chart': { title: 'Line chart', subtitle: 'Multi series' },
    '/data-visualization/radar-chart/single-series': { title: 'Radar chart', subtitle: 'Single series' },
    '/data-visualization/radar-chart/multi-series': { title: 'Radar chart', subtitle: 'Multi series' },
    '/data-visualization/scatter-plot/single-series': { title: 'Scatter plot', subtitle: 'Single series' },
    '/data-visualization/scatter-plot/multi-series': { title: 'Scatter plot', subtitle: 'Multi series' },
    '/data-visualization/stacked-area-chart': { title: 'Stacked area chart' },
    '/data-visualization/verticle-bar-chart/single-series': { title: 'Vertical bar chart', subtitle: 'Single series' },
    '/data-visualization/verticle-bar-chart/grouped-series': { title: 'Vertical bar chart', subtitle: 'Grouped series' },
    '/data-visualization/verticle-bar-chart/stacked-series': { title: 'Vertical bar chart', subtitle: 'Stacked series' },
    '/data-visualization/horizontal-bar-chart/single-series': { title: 'Horizontal bar chart', subtitle: 'Single series' },
    '/data-visualization/horizontal-bar-chart/grouped-series': { title: 'Horizontal bar chart', subtitle: 'Grouped series' },
    '/data-visualization/horizontal-bar-chart/stacked-series': { title: 'Horizontal bar chart', subtitle: 'Stacked series' },
    '/data-visualization/histogram/single-series': { title: 'Histogram', subtitle: 'Single series' },
    '/data-visualization/histogram/multi-series': { title: 'Histogram', subtitle: 'Multi series' },
    '/data-visualization/heatmap': { title: 'Heatmap' },
    '/data-visualization/topology/hierarchy-chart': { title: 'Hierarchy chart' },
    '/data-visualization/topology/logical-view-chart': { title: 'Logical view chart' },
    '/data-visualization/data-markers': { title: 'Data markers' },

    '/hooks': { title: 'Hooks' },
    '/providers': { title: 'Providers' },
    '/release-notes': { title: 'Release notes' },
  };

  useEffect(() => {
    notifications
      .filter((item) => item.new)
      .reverse()
      .forEach((item) => {
        const notification = new Notification({
          title: item.title,
          description: item.description,
          timeout: 4000,
          action: () => {
            acknowledgeNotification();
            localStorage.updateObject('notifications', {
              new: Date.now(),
            });
          },
        });
        notification.init();
      });
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location, setCurrentPath]);

  const navigation = (
    <Navigation>
      <NavItem active={currentPath === '/about'}><Link to="/about">About</Link></NavItem>
      <NavGroup title="Example" active={currentPath.startsWith('/example')}>
        <NavItem active={currentPath === '/example/simple-app'}><Link to="/example/simple-app">Simple application</Link></NavItem>
        <NavItem active={currentPath === '/example/multi-page-app'}><Link to="/example/multi-page-app">Multi page application</Link></NavItem>
      </NavGroup>
      <NavGroup title="Components" active={currentPath.startsWith('/components')}>
        <NavItem active={currentPath === '/components/accordion'}><Link to="/components/accordion">Accordion</Link></NavItem>
        <NavItem active={currentPath === '/components/breadcrumbs'}><Link to="/components/breadcrumbs">Breadcrumb</Link></NavItem>
        <NavItem active={currentPath === '/components/buttons'}><Link to="/components/buttons">Buttons</Link></NavItem>
        <NavItem active={currentPath === '/components/cards'}><Link to="/components/cards">Cards</Link></NavItem>
        <NavItem active={currentPath === '/components/carousel'}><Link to="/components/carousel">Carousel</Link></NavItem>
        <NavItem active={currentPath === '/components/checkboxes'}><Link to="/components/checkboxes">Checkboxes</Link></NavItem>
        <NavItem active={currentPath === '/components/datefields'}><Link to="/components/datefields">Date fields</Link></NavItem>
        <NavItem active={currentPath === '/components/dropdowns'}><Link to="/components/dropdowns">Dropdowns</Link></NavItem>
        <NavItem active={currentPath === '/components/loading'}><Link to="/components/loading">Loading</Link></NavItem>
        <NavItem active={currentPath === '/components/numberfields'}><Link to="/components/numberfields">Number fields</Link></NavItem>
        <NavItem active={currentPath === '/components/pagination'}><Link to="/components/pagination">Pagination</Link></NavItem>
        <NavItem active={currentPath === '/components/pills'}><Link to="/components/pills">Pills</Link></NavItem>
        <NavItem active={currentPath === '/components/progress-bar'}><Link to="/components/progress-bar">Progress bar</Link></NavItem>
        <NavItem active={currentPath === '/components/radiobuttons'}><Link to="/components/radiobuttons">Radio buttons</Link></NavItem>
        <NavItem active={currentPath === '/components/rangesliders'}><Link to="/components/rangesliders">Range sliders</Link></NavItem>
        <NavItem active={currentPath === '/components/search'}><Link to="/components/search">Search fields</Link></NavItem>
        <NavItem active={currentPath === '/components/selects'}><Link to="/components/selects">Selects</Link></NavItem>
        <NavItem active={currentPath === '/components/switch'}><Link to="/components/switch">Switch</Link></NavItem>
        <NavItem active={currentPath === '/components/tables'}><Link to="/components/tables">Tables</Link></NavItem>
        <NavItem active={currentPath === '/components/tabs'}><Link to="/components/tabs">Tabs</Link></NavItem>
        <NavItem active={currentPath === '/components/tags'}><Link to="/components/tags">Tags</Link></NavItem>
        <NavItem active={currentPath === '/components/textfields'}><Link to="/components/textfields">Text fields</Link></NavItem>
        <NavItem active={currentPath === '/components/timeline'}><Link to="/components/timeline">Timeline</Link></NavItem>
        <NavItem active={currentPath === '/components/time-picker'}><Link to="/components/time-picker">Time picker</Link></NavItem>
        <NavItem active={currentPath === '/components/tooltips'}><Link to="/components/tooltips">Tooltips</Link></NavItem>
        <NavItem active={currentPath === '/components/trees'}><Link to="/components/trees">Trees</Link></NavItem>
      </NavGroup>
      <NavGroup title="Layouts" active={currentPath.startsWith('/layouts')}>
        <NavItem active={currentPath === '/layouts/appbar'}><Link to="/layouts/appbar">App bar</Link></NavItem>
        <NavItem active={currentPath === '/layouts/appbody'}><Link to="/layouts/appbody">App body</Link></NavItem>
        <NavItem active={currentPath === '/layouts/appcontainer'}><Link to="/layouts/appcontainer">App container</Link></NavItem>
        <NavItem active={currentPath === '/layouts/dialogs'}><Link to="/layouts/dialogs">Dialogs</Link></NavItem>
        <NavItem active={currentPath === '/layouts/empty-state'}><Link to="/layouts/empty-state">Empty state</Link></NavItem>
        <NavItem active={currentPath === '/layouts/list-builder'}><Link to="/layouts/list-builder">List builder</Link></NavItem>
        <NavItem active={currentPath === '/layouts/flyout'}><Link to="/layouts/flyout">Flyout panel</Link></NavItem>
        <NavItem active={currentPath === '/layouts/multi-panel-tile'}><Link to="/layouts/multi-panel-tile">Multi panel tile</Link></NavItem>
        <NavItem active={currentPath === '/layouts/navigation'}><Link to="/layouts/navigation">Navigation</Link></NavItem>
        <NavItem active={currentPath === '/layouts/notification-log'}><Link to="/layouts/notification-log">Notification log</Link></NavItem>
        <NavItem active={currentPath === '/layouts/systembar'}><Link to="/layouts/systembar">System bar</Link></NavItem>
        <NavItem active={currentPath === '/layouts/tiles'}><Link to="/layouts/tiles">Tiles</Link></NavItem>
        <NavItem active={currentPath === '/layouts/wizard'}><Link to="/layouts/wizard">Wizard</Link></NavItem>
      </NavGroup>
      <NavGroup title="Data Visualization" active={currentPath.startsWith('/data-visualization')}>
        <NavItem active={currentPath === '/data-visualization/box-plot'}><Link to="/data-visualization/box-plot">Box plot</Link></NavItem>
        <NavGroup title="Bubble chart" active={currentPath.startsWith('/data-visualization/bubble-chart')}>
          <NavItem active={currentPath === '/data-visualization/bubble-chart/single-bubble-chart'}><Link to="/data-visualization/bubble-chart/single-bubble-chart">Single series</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/bubble-chart/multi-bubble-chart'}><Link to="/data-visualization/bubble-chart/multi-bubble-chart">Multi series</Link></NavItem>
        </NavGroup>
        <NavItem active={currentPath === '/data-visualization/data-markers'}><Link to="/data-visualization/data-markers">Data markers</Link></NavItem>
        <NavItem active={currentPath === '/data-visualization/donut'}><Link to="/data-visualization/donut">Donut</Link></NavItem>
        <NavItem active={currentPath === '/data-visualization/gauge'}><Link to="/data-visualization/gauge">Gauge</Link></NavItem>
        <NavItem active={currentPath === '/data-visualization/heatmap'}><Link to="/data-visualization/heatmap">Heatmap</Link></NavItem>
        <NavGroup title="Histogram" active={currentPath.startsWith('/data-visualization/histogram')}>
          <NavItem active={currentPath === '/data-visualization/histogram/single-series'}><Link to="/data-visualization/histogram/single-series">Single series</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/histogram/multi-series'}><Link to="/data-visualization/histogram/multi-series">Multi series</Link></NavItem>
        </NavGroup>
        <NavGroup title="Horizontal bar chart" active={currentPath.startsWith('/data-visualization/horizontal-bar-chart')}>
          <NavItem active={currentPath === '/data-visualization/horizontal-bar-chart/single-series'}><Link to="/data-visualization/horizontal-bar-chart/single-series">Single series</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/horizontal-bar-chart/grouped-series'}><Link to="/data-visualization/horizontal-bar-chart/grouped-series">Grouped series</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/horizontal-bar-chart/stacked-series'}><Link to="/data-visualization/horizontal-bar-chart/stacked-series">Stacked series</Link></NavItem>
        </NavGroup>
        <NavGroup title="Line chart" active={currentPath.startsWith('/data-visualization/line-chart')}>
          <NavItem active={currentPath === '/data-visualization/line-chart/single-line-chart'}><Link to="/data-visualization/line-chart/single-line-chart">Single series</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/line-chart/multi-line-chart'}><Link to="/data-visualization/line-chart/multi-line-chart">Multi series</Link></NavItem>
        </NavGroup>
        <NavGroup title="Radar chart" active={currentPath.startsWith('/data-visualization/radar-chart')}>
          <NavItem active={currentPath === '/data-visualization/radar-chart/single-series'}><Link to="/data-visualization/radar-chart/single-series">Single series</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/radar-chart/multi-series'}><Link to="/data-visualization/radar-chart/multi-series">Multi series</Link></NavItem>
        </NavGroup>
        <NavGroup title="Scatter plot" active={currentPath.startsWith('/data-visualization/scatter-plot')}>
          <NavItem active={currentPath === '/data-visualization/scatter-plot/single-series'}><Link to="/data-visualization/scatter-plot/single-series">Single series</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/scatter-plot/multi-series'}><Link to="/data-visualization/scatter-plot/multi-series">Multi series</Link></NavItem>
        </NavGroup>
        <NavItem active={currentPath === '/data-visualization/stacked-area-chart'}><Link to="/data-visualization/stacked-area-chart">Stacked area chart</Link></NavItem>
        <NavGroup title="Topology" active={currentPath.startsWith('/data-visualization/topology')}>
          <NavItem active={currentPath === '/data-visualization/topology/hierarchy-chart'}><Link to="/data-visualization/topology/hierarchy-chart">Hierarchy chart</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/topology/logical-view-chart'}><Link to="/data-visualization/topology/logical-view-chart">Logical view chart</Link></NavItem>
        </NavGroup>
        <NavGroup title="Vertical bar chart" active={currentPath.startsWith('/data-visualization/verticle-bar-chart')}>
          <NavItem active={currentPath === '/data-visualization/verticle-bar-chart/single-series'}><Link to="/data-visualization/verticle-bar-chart/single-series">Single series</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/verticle-bar-chart/grouped-series'}><Link to="/data-visualization/verticle-bar-chart/grouped-series">Grouped series</Link></NavItem>
          <NavItem active={currentPath === '/data-visualization/verticle-bar-chart/stacked-series'}><Link to="/data-visualization/verticle-bar-chart/stacked-series">Stacked series</Link></NavItem>
        </NavGroup>
      </NavGroup>
      <NavItem active={currentPath === '/hooks'}><Link to="/hooks">Hooks</Link></NavItem>
      <NavItem active={currentPath === '/providers'}><Link to="/providers">Providers</Link></NavItem>
      <NavItem active={currentPath === '/release-notes'}><Link to="/release-notes">Release notes</Link></NavItem>
    </Navigation>
  );
  const pageTitle = (pathToTitleMap[currentPath] && pathToTitleMap[currentPath].title) || '';
  const pageSubtitle = pathToTitleMap[currentPath] && pathToTitleMap[currentPath].subtitle;

  return (
    <>
      <SystemBar product="React EDS" acronym="React EDS" homeLink="#/about">
        <SystemBarComponent>
          {/* <a href="https://gitlab.seld.rnd.ericsson.se/EKRISVA/react-eds/blob/master/CHANGELOG.md" rel="noopener noreferrer" target="_blank">{`v${version}`}</a> */}
        </SystemBarComponent>
        <NotificationTrigger
          notifications={newNotifications}
          onClick={() => {
            toggleSysPanel('notifications');
            acknowledgeNotification();
            localStorage.updateObject('notifications', {
              new: Date.now(),
            });
          }}
        />
        <SystemBarComponent onClick={() => toggleSysPanel('userSettings')}>
          <i className="icon icon-profile" />
          <span className="user">Username</span>
        </SystemBarComponent>
      </SystemBar>
      <AppContainer
        showSystemPanel={showSysPanel}
        systemPanels={[
          <SysPanel
            key="userPanel"
            hidden={!sysPanel.userSettings}
            onClose={() => closeSysPanel('userSettings')}
          />,
          <NotificationLogPanel
            key="notificationPanel"
            hidden={!sysPanel.notifications}
            onClose={() => {
              closeSysPanel('notifications');
              readNotification();
              localStorage.saveObject('notifications', {
                new: Date.now(),
                viewed: Date.now(),
              });
            }}
          >
            {notifications.map((item) => <NotificationLogItem key={randomKey()} isNew={item.viewed} title={item.title} description={item.description} timestamp={item.timestamp} />)}
          </NotificationLogPanel>,
        ]}
      >
        <AppBar subtitle={pageSubtitle}>{pageTitle}</AppBar>
        <AppBody navigation={navigation}>
          <Suspense>
            <Switch>
              <Route exact path="/" render={() => (<Redirect to="/about" />)} />
              <Route path="/about">
                <AboutPage />
              </Route>
              <Route path="/example/simple-app">
                <ExampleSimplePage />
              </Route>
              <Route path="/example/multi-page-app">
                <ExampleMultiPage />
              </Route>
              <Route path="/components/accordion">
                <AccordionPage />
              </Route>
              <Route path="/components/breadcrumbs">
                <BreadcrumbsPage />
              </Route>
              <Route path="/components/trees">
                <TreesPage />
              </Route>
              <Route path="/components/buttons">
                <ButtonsPage />
              </Route>
              <Route path="/components/cards">
                <CardsPage />
              </Route>
              <Route path="/components/carousel">
                <CarouselPage />
              </Route>
              <Route path="/components/checkboxes">
                <CheckboxesPage />
              </Route>
              <Route path="/components/radiobuttons">
                <RadioButtonsPage />
              </Route>
              <Route path="/components/switch">
                <SwitchesPage />
              </Route>
              <Route path="/components/dropdowns">
                <DropdownsPage />
              </Route>
              <Route path="/components/loading">
                <LoadingPage />
              </Route>
              <Route path="/components/rangesliders">
                <RangeSlidersPage />
              </Route>
              <Route path="/components/selects">
                <SelectsPage />
              </Route>
              <Route path="/components/textfields">
                <TextFieldsPage />
              </Route>
              <Route path="/components/time-picker">
                <TimePickerPage />
              </Route>
              <Route path="/components/numberfields">
                <NumberFieldsPage />
              </Route>
              <Route path="/components/datefields">
                <DateFieldsPage />
              </Route>
              <Route path="/components/progress-bar">
                <ProgressBarsPage />
              </Route>
              <Route path="/components/tables">
                <TablesPage />
              </Route>
              <Route path="/components/pagination">
                <PaginationPage />
              </Route>
              <Route path="/components/search">
                <SearchFieldsPage />
              </Route>
              <Route path="/components/pills">
                <PillsPage />
              </Route>
              <Route path="/components/tabs">
                <TabsPage />
              </Route>
              <Route path="/components/tags">
                <TagsPage />
              </Route>
              <Route path="/components/tooltips">
                <TooltipsPage />
              </Route>
              <Route path="/components/timeline">
                <TimelinePage />
              </Route>
              <Route path="/data-visualization/gauge">
                <GaugePage />
              </Route>
              <Route path="/data-visualization/heatmap">
                <HeatmapPage />
              </Route>
              <Route path="/data-visualization/donut">
                <DonutPage />
              </Route>
              <Route path="/data-visualization/box-plot">
                <BoxPlotPage />
              </Route>
              <Route path="/data-visualization/line-chart/single-line-chart">
                <LineSingleSeriesPage />
              </Route>
              <Route path="/data-visualization/line-chart/multi-line-chart">
                <LineMultiSeriesPage />
              </Route>
              <Route path="/data-visualization/radar-chart/single-series">
                <RadarSingleSeriesPage />
              </Route>
              <Route path="/data-visualization/radar-chart/multi-series">
                <RadarMultiSeriesPage />
              </Route>
              <Route path="/data-visualization/scatter-plot/single-series">
                <ScatterPlotSingleSeriesPage />
              </Route>
              <Route path="/data-visualization/scatter-plot/multi-series">
                <ScatterPlotMultiSeriesPage />
              </Route>
              <Route path="/data-visualization/verticle-bar-chart/single-series">
                <VerticalBarSingleSeriesPage />
              </Route>
              <Route path="/data-visualization/verticle-bar-chart/grouped-series">
                <VerticalBarGroupedSeriesPage />
              </Route>
              <Route path="/data-visualization/verticle-bar-chart/stacked-series">
                <VerticalBarStackedSeriesPage />
              </Route>
              <Route path="/data-visualization/horizontal-bar-chart/single-series">
                <HorizontalBarSingleSeriesPage />
              </Route>
              <Route path="/data-visualization/horizontal-bar-chart/grouped-series">
                <HorizontalBarGroupedSeriesPage />
              </Route>
              <Route path="/data-visualization/horizontal-bar-chart/stacked-series">
                <HorizontalBarStackedSeriesPage />
              </Route>
              <Route path="/data-visualization/histogram/single-series">
                <HistogramSingleSeriesPage />
              </Route>
              <Route path="/data-visualization/histogram/multi-series">
                <HistogramMultiSeriesPage />
              </Route>
              <Route path="/data-visualization/bubble-chart/single-bubble-chart">
                <BubbleSingleSeriesPage />
              </Route>
              <Route path="/data-visualization/bubble-chart/multi-bubble-chart">
                <BubbleMultiSeriesPage />
              </Route>
              <Route path="/data-visualization/topology/hierarchy-chart">
                <HierarchyChartPage />
              </Route>
              <Route path="/data-visualization/stacked-area-chart">
                <StackedAreaChartPage />
              </Route>
              <Route path="/data-visualization/topology/logical-view-chart">
                <LogicalViewChartPage />
              </Route>
              <Route path="/data-visualization/data-markers">
                <DataMarkersPage />
              </Route>
              <Route path="/layouts/systembar">
                <SystemBarPage />
              </Route>
              <Route path="/layouts/appcontainer">
                <AppContainerPage />
              </Route>
              <Route path="/layouts/appbar">
                <AppBarPage />
              </Route>
              <Route path="/layouts/appbody">
                <AppBodyPage />
              </Route>
              <Route path="/layouts/dialogs">
                <DialogsPage />
              </Route>
              <Route path="/layouts/empty-state">
                <EmptyStatePage />
              </Route>
              <Route path="/layouts/list-builder">
                <ListBuilderPage />
              </Route>
              <Route path="/layouts/tiles">
                <TilesPage />
              </Route>
              <Route path="/layouts/multi-panel-tile">
                <MultiPanelTilePage />
              </Route>
              <Route path="/layouts/notification-log">
                <NotificationLogPage notifications={notifications} addNotifications={addNotifications} />
              </Route>
              <Route path="/layouts/wizard">
                <WizardPage />
              </Route>
              <Route path="/layouts/flyout">
                <FlyoutPage />
              </Route>
              <Route path="/layouts/navigation">
                <NavigationPage />
              </Route>
              <Route path="/hooks">
                <HooksPage />
              </Route>
              <Route path="/providers">
                <ProvidersPage />
              </Route>
              <Route path="/release-notes">
                <ReleaseNotesPage />
              </Route>
            </Switch>
          </Suspense>
        </AppBody>
      </AppContainer>
    </>
  );
}

export default App;
